import { html, LitElement } from "lit";
import { customElement, property } from "lit/decorators";
import "../../voyage-popup/src/voyage-popup";
import { SvgIcons } from "../../voyage-svg-icons/src/voyage-svg-icons";
import { legendData, legendDataExtended } from "../helper";
import { PolarPlotResponse } from "../types/polar-schema-types";
import { polarPlotChartStyles } from "../style/polar-chart-styles";
import {
  chartColors,
  defaultZoneVisibility,
  RiskZonesEnum,
  ZoneVisibility,
  ZoneVisibilityKey,
} from "../types/polar-chart-types";
import { getRiskPolygonsToRender } from "../utils/cartesian-utils";

declare const echarts: any;
@customElement('voyage-polar-plot')
export class VoyagePolarPlot extends LitElement {
 @property({type: Boolean}) shown: boolean = false;
 @property({attribute: false}) currentDegree: number = 0;
 @property({attribute: false}) newDegree: number | null = null;
 @property({attribute: false}) newSpeed: number | null = null;
 @property({attribute: false}) SWDegree: number | null = null;
 @property({attribute: false}) WDDegree: number | null = null;
 @property({attribute: false}) WVDegree: number | null = null;
 @property({ attribute: false }) SWPosition = { x: 0, y: 0 };
 @property({ attribute: false }) WDPosition = { x: 0, y: 0 };
 @property({ attribute: false }) WVPosition = { x: 0, y: 0 };
 @property({ attribute: false }) isEdited = false;
 @property({ attribute: false }) selectedView: string = "NorthUp";
 @property({ attribute: false }) zoneVisibility: ZoneVisibility = { ...defaultZoneVisibility };
 @property({ type: Array }) polarPlotData:
        | PolarPlotResponse
        | undefined
        | null = null

 private chart: any = null;
 private readonly resizeChart = () => this.chart?.resize();

 static styles = polarPlotChartStyles;

 constructor() {
  super();
  this.transformAndPlotRiskPolygons = this.transformAndPlotRiskPolygons.bind(this);
}

getIconPosition(angle: number) {
 const finalAngle = -angle + 90;
 const theta = finalAngle * (Math.PI / 180); // Convert degrees to radians
 const x = (235 * Math.cos(theta));
 const y = (235 * Math.sin(theta));
 return { x, y };
}

private transformPolygons(polygonData: number[][]) {
     return polygonData.map(point => {
         const speed = point[0];
         let degree = point[1];
         if (degree < 0) degree += 360;  
         return [speed, degree] ;
     });
}

private getZoneVisibilityKey(label: string): ZoneVisibilityKey | null {
  switch (label) {
    case 'Heavy Rolling Zone':
      return 'heavyRollingZone';
    case 'Parametric Roll Zone':
      return 'parametricRollZone';
    case 'High Acceleration Force Zone':
      return 'transverseAccelerationZone';
    default:
      return null;
  }
}

private isZoneVisible(zone: ZoneVisibilityKey) {
  return this.zoneVisibility?.[zone] ?? true;
}

private handleLegendToggle(event: Event, zone: ZoneVisibilityKey) {
  const checked = (event.target as HTMLInputElement).checked;
  this.dispatchEvent(new CustomEvent('zone-visibility-change', {
    detail: { zone, visible: checked },
    bubbles: true,
    composed: true,
  }));
}


render() {
  const rotationDegree = this.selectedView === "HeadUp" ? this.currentDegree : 0;
  return html`
   <div class="polar-container">
      <div class="chart-container">
        <div class="legend">
          <div class="legend left-legend">
            ${this.renderLeftLegend()}
          </div>
          <div class="legend right-legend">
            ${this.renderRightLegend()}
          </div>
        </div>
        <div class="polar-chart-contents" style="transform: rotate(-${rotationDegree}deg);">
          <div class="circle-band"></div>
          ${Array.from({ length: 4 }).map(
          (_, i) => html`<div class="north-dash" style="transform: rotate(${i * 90}deg) translateX(200px);"></div>`
        )}
          ${Array.from({ length: 12 }).map(
          (_, i) => html`<div class="degree-dash" style="transform: rotate(${i * 30}deg) translateX(200px);"></div>`
        )}
        ${Array.from({ length: 72 }).map(
          (_, i) => html`<div class="degree-dash-axis" style="transform: rotate(${i * 5}deg) translateX(200px);"></div>`
        )}
          ${Array.from({ length: 12 }).map(
            (_, i) =>
              html`<div
                class="degree-label"
                style="transform: rotate(${i * 30}deg) translateX(200px) rotate(-${i * 30}deg);"
              >
                <div style="transform: rotate(${rotationDegree}deg);">${(i * 30 + 90) % 360}°</div>
              </div>`
          )}
          <div class="label n" style="transform: translate(-50%, -50%) rotate(${rotationDegree}deg);">N</div>
          <div class="label e" style="transform: translate(-50%, -50%) rotate(${rotationDegree}deg);">E</div>
          <div class="label s" style="transform: translate(-50%, -50%) rotate(${rotationDegree}deg);">S</div>
          <div class="label w" style="transform: translate(-50%, -50%) rotate(${rotationDegree}deg);">W</div>
          <div id="chartContainer"></div>
          <div id="icon-wrapper" style="left: calc(50% + ${this.SWPosition.x}px); top: calc(50% - ${this.SWPosition.y}px); transform: translate(-50%, -50%) rotate(${this.SWDegree}deg);">${SvgIcons.SWIcon}</div>
          <div id="icon-wrapper" style="left: calc(50% + ${this.WDPosition.x}px); top: calc(50% - ${this.WDPosition.y}px); transform: translate(-50%, -50%) rotate(${this.WDDegree}deg);">${SvgIcons.WDIcon}</div>
          <div id="icon-wrapper" style="left: calc(50% + ${this.WVPosition.x}px); top: calc(50% - ${this.WVPosition.y}px); transform: translate(-50%, -50%) rotate(${this.WVDegree}deg);">${SvgIcons.WVIcon}</div>
          <div class="svg" style="transform: translate(-50%, -50%) rotate(${this.currentDegree}deg);">${SvgIcons.polarVesselIcon}</div>
        </div>
     </div>
   </div>
  `;
}

renderLeftLegend() {
 const colors = this.polarPlotData?.critical_roll_model === "MLModel" ? legendDataExtended : legendData;
 const zoneItems = colors.filter(item => !['Wind', 'Wave', 'Swell'].includes(item.label));
 const riskItems = zoneItems.filter(item => this.getZoneVisibilityKey(item.label));
 const visibleRiskCount = riskItems.filter(item => {
   const zoneKey = this.getZoneVisibilityKey(item.label);
   return zoneKey ? this.isZoneVisible(zoneKey) : false;
 }).length;

 return html`
   <div class="legend-heading">
     You can easily show or hide layers (${visibleRiskCount}/${riskItems.length}):
   </div>
   ${zoneItems.map(
     item => {
       const zoneKey = this.getZoneVisibilityKey(item.label);
       return html`
       <div class="legend-item">
         ${zoneKey
          ? html`<input
              class="legend-checkbox"
              type="checkbox"
              .checked=${this.isZoneVisible(zoneKey)}
              @change=${(event: Event) => this.handleLegendToggle(event, zoneKey)}
            >`
          : html`<span class="legend-checkbox-spacer"></span>`}
         <div
           class="legend-color ${item.label === 'High Acceleration Force Zone' ? 'high-acceleration-color' : ''}"
           style="background-color: ${item.color};"
         ></div>
         <span>${item.label}</span>
       </div>
     `}
   )}
 `;
}

renderRightLegend() {
 return html`
   <div class="legend-item">
     <div class="legend-line solid-line"></div>
     <span>Current Vessel Details</span>
   </div>
   <div class="legend-item">
     <div class="legend-line dashed-line"></div>
     <span>New Vessel Details</span>
   </div>
   ${[
     { label: 'Wind', abbreviation: 'Wd' },
     { label: 'Wave', abbreviation: 'Wv' },
     { label: 'Swell', abbreviation: 'Sw' },
   ].map(item => html`
     <div class="legend-item weather-legend-item">
       <div class="legend-weather-icon">${this.getSvgIcon(item.label)}</div>
       <span>${item.abbreviation}</span>
     </div>
   `)}
 `;
}

getSvgIcon(label: string) {
  switch (label) {
    case 'Wind':
      return html`${SvgIcons.WDIcon}`;
    case 'Wave':
      return html`${SvgIcons.WVIcon}`;
    case 'Swell':
      return html`${SvgIcons.SWIcon}`;
    default:
      return null;
  }
}

firstUpdated() {
  this.initializeChart();
}

connectedCallback() {
  super.connectedCallback();
  if (this.hasUpdated) {
    this.initializeChart();
    this.transformAndPlotRiskPolygons();
  }
}

private initializeChart() {
  if (this.chart) return;

  const chartContainer = this.shadowRoot?.getElementById("chartContainer") as HTMLElement;
  if (chartContainer) {
    this.chart = echarts.init(chartContainer);
    window.addEventListener('resize', this.resizeChart);
  }
}

disconnectedCallback() {
  window.removeEventListener('resize', this.resizeChart);
  this.chart?.dispose();
  this.chart = null;
  super.disconnectedCallback();
}

updated(changedProperties: Map<string | number | symbol, unknown>) {
 if (changedProperties.has('polarPlotData') || changedProperties.has('zoneVisibility')) {
   this.transformAndPlotRiskPolygons();
 } else if (
   changedProperties.has('newDegree') ||
   changedProperties.has('newSpeed') ||
   changedProperties.has('isEdited')
 ) {
   this.updateNewVesselMarker();
 }

 if (changedProperties.has('SWDegree')) {
  this.SWPosition = this.getIconPosition(this.SWDegree ?? 0);
 }
 if (changedProperties.has('WDDegree')) {
   this.WDPosition = this.getIconPosition(this.WDDegree ?? 0);
 }
 if (changedProperties.has('WVDegree')) {
   this.WVPosition = this.getIconPosition(this.WVDegree ?? 0);
 }
}

// Builds the "New Vessel Details" marker series. Shared by the full
// polygon rebuild and the lightweight marker-only update so both stay
// in sync.
private buildNewVesselMarkerSeries() {
  const currentDirection = this.polarPlotData?.course_over_ground;
  const currentSpeed = this.polarPlotData?.speed_over_ground;
  const shouldShow = (this.newDegree !== currentDirection || this.newSpeed !== currentSpeed) && this.isEdited;

  return {
    id: 'new-vessel-details',
    name: 'New Vessel Details',
    type: 'line',
    coordinateSystem: 'polar',
    data: shouldShow ? [[this.newSpeed, this.newDegree],[0,0]] : [],
    lineStyle: { width: 3, color: 'rgba(86, 130, 236, 1)', type: 'dashed' },
    itemStyle: {
      color: 'rgba(66, 176, 213, 1)',
      borderColor: 'rgba(0, 0, 0, 0.2)',
      borderWidth: 2
    },
    symbol: 'circle',
    symbolSize: 15,
    zlevel: 12,
  };
}

// Risk-zone polygons only depend on polarPlotData/zoneVisibility, which
// don't change from heading/speed adjustment alone - only this marker
// moves. Merge just this one series in place (by id) instead of tearing
// down and rebuilding every zone polygon on every heading/speed tick.
private updateNewVesselMarker() {
  if (!this.chart) return;
  this.chart.setOption({ series: [this.buildNewVesselMarkerSeries()] });
}

private transformAndPlotRiskPolygons() {
 const data = this.polarPlotData;

 if (!data) {
  return;
 }
 const echartsData: any = [];

 echartsData.push({
   name: 'Speed Constraint Zone',
   type: 'line',
   coordinateSystem: 'polar',
   data: Array.from({ length: 370 }, (_, i) => [30, i]),
   areaStyle: { color: '#DEE8F6'},
   lineStyle: { width: 0 },
   symbol: 'none',
   zlevel: 1,
   silent: false,
   emphasis: {
    disabled: true,
   },
  });

 const minSpeedPolygon = data.minimum_speed_polygon;
 const maxSpeedPolygon = data.maximum_speed_polygon;
 const safeZonePolygon = minSpeedPolygon.concat(maxSpeedPolygon.slice().reverse());
 const heavyRollingZonePolygon = data.roll_risk_polygons;
 const parametricRollZonePolygon = data.parametric_roll_risk_polygons || [];
 const transverseAccelerationZonePolygon = data.transverse_acceleration_risk_polygons || [];
 const { critical_roll_model } = data;

const { parametricRollPolygonsToRender, heavyRollingPolygonsToRender } = getRiskPolygonsToRender({
    heavyRollingPolygons: heavyRollingZonePolygon,
    parametricRollRiskPolygons: parametricRollZonePolygon,
    criticalRollModel: critical_roll_model,
});


 echartsData.push({
   name: 'Safe Zone',
   type: 'line',
   coordinateSystem: 'polar',
   data: safeZonePolygon,
   areaStyle: { color: '#aad5ce' },
   lineStyle: { width: 0 },
   symbol: 'none',
   zlevel: 2,
   silent: false,
   emphasis: {
    disabled: true,
   },
  });
  // Render adjusted heavy rolling zones (orange - with parametric overlaps removed)
  if (this.isZoneVisible('heavyRollingZone') && Array.isArray(heavyRollingPolygonsToRender) && heavyRollingPolygonsToRender.length > 0) {
      heavyRollingPolygonsToRender.forEach((polygon: any) => {
      const transformedPolygon = this.transformPolygons(polygon);
          echartsData.push({
              name: RiskZonesEnum["heavyRolling"],
              type: 'line',
              coordinateSystem: 'polar',
              data: transformedPolygon,
              areaStyle: { color: chartColors.heavyRollingZone_light,
                opacity: 0.7
               },
              lineStyle: { width: 0 },
              symbol: 'none',
              zlevel: 3,
              silent: false,
              emphasis: {
                disabled: true,
              },
          });
      });
  }
  if (this.isZoneVisible('parametricRollZone') && Array.isArray(parametricRollPolygonsToRender) && parametricRollPolygonsToRender.length > 0) {
      parametricRollPolygonsToRender.forEach((polygon: any) => {
      const transformedPolygon = this.transformPolygons(polygon);
          echartsData.push({
              name: RiskZonesEnum["parametricRoll"],
              type: 'line',
              coordinateSystem: 'polar',
              data: transformedPolygon,
              areaStyle: {
                color: critical_roll_model === 'TopTier' ? chartColors.parametricRollZone_light : chartColors.parametricRollZone,
                opacity: critical_roll_model === 'TopTier' ? 0.7 : 0.9
              },
              lineStyle: { width: 0 },
              symbol: 'none',
              zlevel: 4,
              silent: false,
              emphasis: {
                disabled: true,
              },
          });
      });
  }
  if (this.isZoneVisible('transverseAccelerationZone') && Array.isArray(transverseAccelerationZonePolygon) && transverseAccelerationZonePolygon.length > 0) {
      transverseAccelerationZonePolygon.forEach((polygon: any) => {
      const transformedPolygon = this.transformPolygons(polygon);
          echartsData.push({
              name: RiskZonesEnum["transverseAcceleration"],
              type: 'line',
              coordinateSystem: 'polar',
              data: transformedPolygon,
              areaStyle: {
                color: chartColors.transverseAccelerationZone_light,
                opacity: 0.45,
              },
              lineStyle: {
                width: 2,
                color: chartColors.transverseAccelerationZone_light,
                opacity: 0.79,
                type: 'dotted',
              },
              symbol: 'none',
              zlevel: 5,
              silent: false,
              emphasis: {
                disabled: true,
              },
          });
      });
  }

 const currentDirection = this.polarPlotData?.course_over_ground;
 const currentSpeed = this.polarPlotData?.speed_over_ground;
 this.currentDegree = currentDirection ?? 0;

 echartsData.push({
  name: 'Current Vessel Details', 
  type: 'line',
  coordinateSystem: 'polar',
  data: [[currentSpeed, currentDirection],[0,0]],
  lineStyle: { width: 3, color: 'rgba(2, 115, 173, 1)' },
  itemStyle: { 
    color: 'rgba(61, 106, 214, 1)',
    borderColor: 'rgba(0, 0, 0, 0.2)',
    borderWidth: 2
  },
  symbol: 'circle', 
  symbolSize: 15,
  zlevel: 12,
 });

 echartsData.push(this.buildNewVesselMarkerSeries());



 const option = {
     polar: {},
     angleAxis: {
      type: "value",
      startAngle: 90,
      min: 0,
      max: 360,
      interval: 30,
      axisLine: {
        show: true,
        lineStyle: {
          width: 1.5,
          type: "dashed", // Dashed line for angle axis
          opacity: 0.1
        },
      },
      axisLabel: {
        show: false, // Hide labels on the angle axis
      },
      splitLine: {
        lineStyle: {
          width: 1.5,
          color: "black",
          opacity: 0.1,
          type: "dashed", // Dashed split lines
        },
      },
      zlevel: 5,
    },
     radiusAxis: {
      nameGap: 54,
      type: "value",
      min: 0,
      max: 25,
      interval: 5, 
      axisLine: {
        show: true,
        lineStyle: {
         color: "black",
          type: "dashed",// Dashed line for radius axis
          opacity: 0.1,
        },
      },
      axisLabel: {
        formatter: '{value}kt',
        color: "black",
        opacity: 0.5,
        rotate: this.selectedView === "HeadUp" ? -(this.currentDegree) : 30,
        margin: 8,
      },
      splitLine: {
        lineStyle: {
          width: 1.5, 
          color: "black",
          opacity: 0.1,
          type: "dashed", // Dashed radius split lines
        },
      },
      zlevel: 5,
    },
     series: echartsData,
     tooltip: {
      trigger: 'item', 
      backgroundColor: 'none', 
      shadowBlur: 0,           
      shadowColor: 'transparent', 
      borderWidth: 0,          
      textStyle: {
          color: 'inherit',
      },
      animation: false,
      formatter: (params: any) => {
        const rotationDegree = this.selectedView === "HeadUp" ? (this.currentDegree) : 0;
        const zoneTooltipLabels = [
          'Heavy Rolling Zone',
          'Parametric Roll Zone',
          'High Acceleration Force Zone',
          'Safe Zone',
          'Speed Constraint Zone',
        ];

        if (zoneTooltipLabels.includes(params.seriesName)) {
          return `<div class="custom-tooltip" style="transform: rotate(${rotationDegree}deg);">
            <div class="tooltip-content">${params.seriesName}</div>
          </div>`;
        }

        if (params.seriesName === 'Current Vessel Details') {
          const [currentSpeed, currentDirection] = params.value;
          return `<div class="custom-tooltip" style="transform: rotate(${rotationDegree}deg);">
            <div class="tooltip-content">
            Vessel Speed (current): <strong>${currentSpeed.toFixed(2)} kt</strong><br>Vessel Direction (current): <strong>${currentDirection.toFixed(2)}°</strong>
            </div>
          </div>`;
        }
        if (params.seriesName === 'New Vessel Details') {
         const [newSpeed, newDirection] = params.value;
         return `<div class="custom-tooltip" style="transform: rotate(${rotationDegree}deg);">
         <div class="tooltip-content">
         Vessel Speed (new): <strong>${newSpeed.toFixed(2)} kt</strong><br>Vessel Direction (new): <strong>${newDirection.toFixed(2)}°</strong>
         </div>
         </div>`;
       }
      },
      position: (
        point: number[],
        _: any,
        __: HTMLElement,
        ___: any,
        size: any,
      ) => {
        const [x, y] = point;
        const { contentSize } = size;
        return [x + 16, y - contentSize[1] / 2];
      },
      z: 100,
    },
 };

 if (this.chart) {
   this.chart.setOption(option, { replaceMerge: ['series'] });
 }
}
}
